import React, { useEffect, useState } from "react";
import axios from 'axios';
import { parse, format } from 'date-fns';
import { Pencil, Trash2, XCircle, Calendar } from 'lucide-react';

export default function Settings() {
    const [showForm, setShowForm] = useState(false);
    const [showTokenForm, setShowTokenForm] = useState(false);
    const [showDayOffForm, setShowDayOffForm] = useState(false);
    const [holiday, setHoliday] = useState([]);
    const [tokens, setTokens] = useState([]);
    const [holidayName, setHolidayName] = useState('');
    const [holidayDate, setHolidayDate] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedDay, setSelectedDay] = useState('');
    const [tokenName, setTokenName] = useState('');
    const [tokenType, setTokenType] = useState('');
    const [tokenValue, setTokenValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingToken, setEditingToken] = useState(null);
    const [holidayCurrentPage, setHolidayCurrentPage] = useState(1);
    const [tokenCurrentPage, setTokenCurrentPage] = useState(1);
    const [showCancelLessonsForm, setShowCancelLessonsForm] = useState(false);
    const [cancelDate, setCancelDate] = useState('');
    const itemsPerPage = 15;

    useEffect(() => {
        const fetchHoliday = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getHoliday`);
                setHoliday(response.data.data);
            } catch (error) {
                console.error('Error fetching holiday:', error);
                alert('Failed to fetch holiday.');
            }
        };
        fetchHoliday();
    }, []);

    useEffect(() => {
        const fetchTokens = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getToken`);
                setTokens(response.data.data);
            } catch (error) {
                console.error('Error fetching tokens:', error);
                alert('Failed to fetch tokens.');
            }
        };
        fetchTokens();
    }, []);

    const handleAddHoliday = () => {
        setShowForm(true);
    };

    const handleAddDayOff = () => {
        setShowDayOffForm(true);
    };

    const handleCreateToken = () => {
        setShowTokenForm(true);
        setEditingToken(null);
        setTokenName('');
        setTokenType('');
        setTokenValue('');
    };

    const handleCancelLessonsForDate = async () => {
        if (!cancelDate) {
          alert('Please select a date.');
          return;
        }
      
        if (window.confirm(`Are you sure you want to cancel all pending video and makeup lessons on ${cancelDate}? This will refund tokens and cannot be undone.`)) {
          try {
            const response = await axios.post(
              `${process.env.REACT_APP_API_URL}/admin/cancelAllLessonsForDate`,
              { date: cancelDate }
            );
            alert(response.data.message);
            setShowCancelLessonsForm(false);
            setCancelDate('');
          } catch (error) {
            console.error('Error cancelling lessons:', error);
            alert(error.response?.data?.error || 'Failed to cancel lessons.');
          }
        }
      };

    const handleHolidaySubmit = async (e) => {
        e.preventDefault();
        const formData = {
            holiday_name: holidayName,
            holiday: holidayDate,
        };
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/addHoliday`, formData);
            if (response.status === 201) {
                setShowForm(false);
                setHolidayName('');
                setHolidayDate('');
                const updatedHolidays = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getHoliday`);
                setHoliday(updatedHolidays.data.data);
                setHolidayCurrentPage(1); // Reset to first page
            }
        } catch (error) {
            console.error('Error creating holiday:', error);
            alert(error.response?.data?.error || 'Failed to create holiday.');
        }
    };

    const handleDayOffSubmit = async (e) => {
        e.preventDefault();
        const formData = {
            start_date: startDate,
            end_date: endDate,
            day_of_week: selectedDay,
        };
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/addOffDay`, formData);
            if (response.status === 201) {
                setShowDayOffForm(false);
                setStartDate('');
                setEndDate('');
                setSelectedDay('');
                const updatedHolidays = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getHoliday`);
                setHoliday(updatedHolidays.data.data);
                setHolidayCurrentPage(1); // Reset to first page
                alert('Days off added successfully!');
            }
        } catch (error) {
            console.error('Error adding days off:', error);
            alert(error.response?.data?.error || 'Failed to add days off.');
        }
    };

    const handleTokenSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let response;
            if (editingToken) {
                response = await axios.put(`${process.env.REACT_APP_API_URL}/admin/updateToken/${tokenName}`, { token_value: tokenValue });
            } else {
                const formData = {
                    token_name: tokenName,
                    token_type: tokenType,
                    token_value: tokenValue,
                };
                response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/createToken`, formData);
            }
            if (response.status === 200 || response.status === 201) {
                setTokenName('');
                setTokenType('');
                setTokenValue('');
                setShowTokenForm(false);
                setEditingToken(null);
                const updatedTokens = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getToken`);
                setTokens(updatedTokens.data.data);
                setTokenCurrentPage(1); // Reset to first page
            }
        } catch (error) {
            console.error(`Error ${editingToken ? 'updating' : 'creating'} token:`, error);
            alert(error.response?.data?.error || `Failed to ${editingToken ? 'update' : 'create'} token.`);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setShowTokenForm(false);
        setShowDayOffForm(false);
        setHolidayName('');
        setHolidayDate('');
        setStartDate('');
        setEndDate('');
        setSelectedDay('');
        setTokenName('');
        setTokenType('');
        setTokenValue('');
        setEditingToken(null);
    };

    const handleDeleteHoliday = async (holiday_name) => {
        if (window.confirm("Are you sure you want to delete this holiday?")) {
            try {
                await axios.delete(`${process.env.REACT_APP_API_URL}/admin/deleteHoliday/${holiday_name}`);
                const updatedHolidays = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getHoliday`);
                setHoliday(updatedHolidays.data.data);
                setHolidayCurrentPage(1); // Reset to first page
            } catch (error) {
                console.error('Error deleting holiday:', error);
                alert(error.response?.data?.error || 'Failed to delete holiday.');
            }
        }
    };

    const handleDeleteToken = async (token_name) => {
        if (window.confirm("Are you sure you want to delete this token?")) {
            try {
                await axios.delete(`${process.env.REACT_APP_API_URL}/admin/deleteToken/${token_name}`);
                const updatedTokens = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getToken`);
                setTokens(updatedTokens.data.data);
                setTokenCurrentPage(1); // Reset to first page
            } catch (error) {
                console.error('Error deleting token:', error);
                alert(error.response?.data?.error || 'Failed to delete token.');
            }
        }
    };

    const handleEditToken = (token) => {
        setEditingToken(token);
        setTokenName(token.token_name);
        setTokenType(token.token_type);
        setTokenValue(token.token_value);
        setShowTokenForm(true);
    };

    // Pagination logic for holidays
    const holidayIndexOfLast = holidayCurrentPage * itemsPerPage;
    const holidayIndexOfFirst = holidayIndexOfLast - itemsPerPage;
    const currentHolidays = holiday.slice(holidayIndexOfFirst, holidayIndexOfLast);
    const holidayTotalPages = Math.ceil(holiday.length / itemsPerPage);

    // Pagination logic for tokens
    const tokenIndexOfLast = tokenCurrentPage * itemsPerPage;
    const tokenIndexOfFirst = tokenIndexOfLast - itemsPerPage;
    const currentTokens = tokens.slice(tokenIndexOfFirst, tokenIndexOfLast);
    const tokenTotalPages = Math.ceil(tokens.length / itemsPerPage);

    return (
        <div className="flex flex-col min-h-screen overflow-y-auto bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="flex-1 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Settings</h1>
                    <div className="flex flex-wrap gap-2 mt-4 sm:mt-0">
                        <button 
                            onClick={handleAddHoliday}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2"
                        >
                            <span>Add Holiday</span>
                        </button>
                        <button 
                            onClick={handleAddDayOff}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2"
                        >
                            <Calendar size={18} />
                            <span>Add Day Off</span>
                        </button>
                        <button 
                            onClick={handleCreateToken}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2"
                        >
                            <span>Create Token</span>
                        </button>
                            <button
                                onClick={() => setShowCancelLessonsForm(true)}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm flex items-center gap-2"
                            >
                                <XCircle size={18} />
                            <span>Cancel All Lessons for Selected Date</span>
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex flex-col lg:flex-row gap-6">
                    {/* Holidays Section */}
                    <div className="flex-1 bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="p-4 sm:p-6">
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-700 mb-4">Holidays</h2>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-blue-100 sticky top-0">
                                            <th className="border-b border-gray-300 px-4 py-2 text-left text-gray-600 font-semibold">Holiday Name</th>
                                            <th className="border-b border-gray-300 px-4 py-2 text-left text-gray-600 font-semibold">Holiday Date</th>
                                            <th className="border-b border-gray-300 px-4 py-2 text-center text-gray-600 font-semibold">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentHolidays.length > 0 ? currentHolidays.map((holiday, index) => (
                                            <tr key={holiday.holiday_name} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}>
                                                <td className="px-4 py-2">{holiday.holiday_name}</td>
                                                <td className="px-4 py-2">
                                                    {format(parse(holiday.holiday, 'yyyy-MM-dd', new Date()), 'yyyy-MM-dd')}
                                                </td>
                                                <td className="px-4 py-2 text-center">
                                                    <button 
                                                        onClick={() => handleDeleteHoliday(holiday.holiday_name)}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="3" className="px-4 py-2 text-center text-gray-500">
                                                    No holidays found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {/* Holiday Pagination Controls */}
                            {holidayTotalPages > 1 && (
                                <div className="flex justify-between items-center mt-6">
                                    <button
                                        onClick={() => setHolidayCurrentPage(prev => Math.max(prev - 1, 1))}
                                        className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
                                        disabled={holidayCurrentPage === 1}
                                    >
                                        Previous
                                    </button>
                                    <select
                                        value={holidayCurrentPage}
                                        onChange={(e) => setHolidayCurrentPage(Number(e.target.value))}
                                        className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {Array.from({ length: holidayTotalPages }, (_, index) => index + 1).map((page) => (
                                            <option key={page} value={page}>
                                                Page {page} of {holidayTotalPages}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => setHolidayCurrentPage(prev => Math.min(prev + 1, holidayTotalPages))}
                                        className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
                                        disabled={holidayCurrentPage === holidayTotalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tokens Section */}
                    <div className="flex-1 bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="p-4 sm:p-6">
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-700 mb-4">Tokens</h2>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-blue-100 sticky top-0">
                                            <th className="border-b border-gray-300 px-4 py-2 text-left text-gray-600 font-semibold">Token Name</th>
                                            <th className="border-b border-gray-300 px-4 py-2 text-left text-gray-600 font-semibold">Token Type</th>
                                            <th className="border-b border-gray-300 px-4 py-2 text-left text-gray-600 font-semibold">Token Value</th>
                                            <th className="border-b border-gray-300 px-4 py-2 text-center text-gray-600 font-semibold">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentTokens.length > 0 ? currentTokens.map((token, index) => (
                                            <tr key={token.token_name} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}>
                                                <td className="px-4 py-2">{token.token_name}</td>
                                                <td className="px-4 py-2">{token.token_type}</td>
                                                <td className="px-4 py-2">{token.token_value}</td>
                                                <td className="px-4 py-2 text-center flex justify-center gap-2">
                                                    <button 
                                                        onClick={() => handleEditToken(token)}
                                                        className="text-blue-600 hover:text-blue-800"
                                                    >
                                                        <Pencil size={18} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteToken(token.token_name)}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="4" className="px-4 py-2 text-center text-gray-500">
                                                    No tokens found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {/* Token Pagination Controls */}
                            {tokenTotalPages > 1 && (
                                <div className="flex justify-between items-center mt-6">
                                    <button
                                        onClick={() => setTokenCurrentPage(prev => Math.max(prev - 1, 1))}
                                        className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
                                        disabled={tokenCurrentPage === 1}
                                    >
                                        Previous
                                    </button>
                                    <select
                                        value={tokenCurrentPage}
                                        onChange={(e) => setTokenCurrentPage(Number(e.target.value))}
                                        className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {Array.from({ length: tokenTotalPages }, (_, index) => index + 1).map((page) => (
                                            <option key={page} value={page}>
                                                Page {page} of {tokenTotalPages}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => setTokenCurrentPage(prev => Math.min(prev + 1, tokenTotalPages))}
                                        className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
                                        disabled={tokenCurrentPage === tokenTotalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Holiday Form Modal */}
            {showForm && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Add New Holiday</h2>
                        <form onSubmit={handleHolidaySubmit}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Holiday Name</label>
                                    <input
                                        type="text"
                                        value={holidayName}
                                        onChange={(e) => setHolidayName(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Holiday Date</label>
                                    <input
                                        type="date"
                                        value={holidayDate}
                                        onChange={(e) => setHolidayDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Submit
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Day Off Form Modal */}
            {showDayOffForm && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Add Days Off</h2>
                        <form onSubmit={handleDayOffSubmit}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">End Date</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Day of the Week</label>
                                    <select
                                        value={selectedDay}
                                        onChange={(e) => setSelectedDay(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="" disabled>Select Day</option>
                                        <option value="Monday">Monday</option>
                                        <option value="Tuesday">Tuesday</option>
                                        <option value="Wednesday">Wednesday</option>
                                        <option value="Thursday">Thursday</option>
                                        <option value="Friday">Friday</option>
                                        <option value="Saturday">Saturday</option>
                                        <option value="Sunday">Sunday</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Submit
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Token Form Modal */}
            {showTokenForm && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">{editingToken ? 'Edit Token' : 'Create New Token'}</h2>
                        <form onSubmit={handleTokenSubmit}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Token Name</label>
                                    <input
                                        type="text"
                                        value={tokenName}
                                        onChange={(e) => setTokenName(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                        disabled={editingToken}
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Token Type</label>
                                    <select
                                        value={tokenType}
                                        onChange={(e) => setTokenType(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                        disabled={editingToken}
                                    >
                                        <option value="" disabled>Select Type</option>
                                        <option value="Live">Live</option>
                                        <option value="Video">Video</option>
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Token Value</label>
                                    <input
                                        type="text"
                                        value={tokenValue}
                                        onChange={(e) => setTokenValue(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${loading && "opacity-50 cursor-not-allowed"}`}
                                    disabled={loading}
                                >
                                    {loading ? "Submitting..." : editingToken ? "Update" : "Submit"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Cancel Lessons Modal */}
            {showCancelLessonsForm && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Cancel All Pending Lessons on a Date
                </h2>
                <div className="mb-6">
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                    Select Date
                    </label>
                    <input
                    type="date"
                    value={cancelDate}
                    onChange={(e) => setCancelDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                    />
                </div>
                <div className="flex justify-end gap-2">
                    <button
                    type="button"
                    onClick={() => {
                        setShowCancelLessonsForm(false);
                        setCancelDate('');
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
                    >
                    Cancel
                    </button>
                    <button
                    type="button"
                    onClick={handleCancelLessonsForDate}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                    Confirm Cancellation
                    </button>
                </div>
                </div>
            </div>
            )}
        </div>
    );
}